import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { weightedFor } from "../lib/wagerWeighted";
import { Coins, Lock, Save } from "lucide-react";

/**
 * Iter 70 — Player-facing offered-volume editor.
 *
 * Lives on the player dashboard so a player can adjust their commitment
 * post-onboarding (e.g. after admin/counsel flags the offer as unsupported
 * by proofs — the flow explicitly has NO admin override; the player must
 * self-correct). Server re-validates 50%–200% of the SERVER-computed
 * weighted wager for that vertical.
 *
 * Reads/writes:
 *   • GET  /api/players/me → weighted wager + current offeredVolumeByVertical
 *   • PATCH /api/players/me/offered-volume  {vertical, offeredVolume}
 *
 * Iter 194 — weighted derivation moved to the shared `weightedFor` helper
 * so total_only-mode players see the correct band (was hardcoded categorized).
 */

function VerticalEditor({ vertical, weighted, current, onSaved, t }) {
  const [val, setVal] = useState(current ? String(Math.round(current)) : "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setVal(current ? String(Math.round(current)) : ""); }, [current]);

  const bounds = useMemo(() => ({
    min: Math.round((weighted || 0) * 0.5),
    max: Math.round((weighted || 0) * 2),
  }), [weighted]);

  const num = Number(val || 0);
  const gated = !(weighted > 0);
  const err = gated || !val
    ? null
    : num < bounds.min
      ? t("onboarding.offered.belowMin").replace("{min}", bounds.min.toLocaleString())
      : num > bounds.max
        ? t("onboarding.offered.aboveMax").replace("{max}", bounds.max.toLocaleString())
        : null;

  const save = useCallback(async () => {
    if (err || gated) return;
    if (!val || num <= 0) { toast.error(t("dash.offered.enterValue") || "Enter a value."); return; }
    setBusy(true);
    try {
      const { data } = await api.patch("/players/me/offered-volume", { vertical, offeredVolume: num });
      toast.success(t("dash.offered.saved") || "Offered volume updated.");
      onSaved && onSaved(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === "object" && d.message) || apiErrorMessage(e));
    } finally { setBusy(false); }
  }, [err, gated, val, num, vertical, onSaved, t]);

  return (
    <div className="border border-white/10 p-4" data-testid={`dash-offered-${vertical}`}>
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="font-display font-bold text-sm text-[#E8ECF4]">
          {vertical === "casino" ? t("dash.offered.casinoTitle") : t("dash.offered.caseTitle")}
        </h3>
        <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]" data-testid={`dash-offered-weighted-${vertical}`}>
          {t("dash.offered.weighted")}: <span className="text-[#E8ECF4] tabular-nums">{(weighted || 0).toLocaleString()}</span>
        </span>
      </div>
      {gated ? (
        <div className="text-[11px] font-mono text-[#F5B841] py-2" data-testid={`dash-offered-gated-${vertical}`}>{t("dash.offered.noWeighted")}</div>
      ) : (
        <>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">
            {t("onboarding.offered.range").replace("{min}", bounds.min.toLocaleString()).replace("{max}", bounds.max.toLocaleString())}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[#8B97AE] font-mono text-sm">$</span>
            <input
              data-testid={`dash-offered-input-${vertical}`}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={val ? Number(val).toLocaleString() : ""}
              onChange={(e) => setVal(e.target.value.replace(/[^\d]/g, ""))}
              placeholder={t("onboarding.offered.placeholder").replace("{min}", bounds.min.toLocaleString()).replace("{max}", bounds.max.toLocaleString())}
              className="flex-1 rounded-none bg-transparent border border-white/20 h-10 font-mono tabular-nums text-right text-[#E8ECF4] px-3"
              style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
            />
            <button
              data-testid={`dash-offered-save-${vertical}`}
              onClick={save}
              disabled={busy || !!err || !val || num <= 0}
              className="inline-flex items-center gap-1.5 px-3 h-10 bg-[#F5B841] text-[#04150F] font-mono text-[11px] uppercase tracking-widest hover:bg-[#2DD4A7] transition-colors disabled:opacity-40"
            >
              <Save className="w-3 h-3" />{busy ? "…" : t("dash.offered.save")}
            </button>
          </div>
          {err && <div className="mt-2 text-[11px] font-mono text-[#F26D6D]" data-testid={`dash-offered-error-${vertical}`}>{err}</div>}
          <div className="mt-2 text-[10px] font-mono text-[#8B97AE]" data-testid={`dash-offered-current-${vertical}`}>
            {t("dash.offered.current")}: <span className="text-[#F5B841] tabular-nums">{(current || 0).toLocaleString()}</span>
          </div>
        </>
      )}
    </div>
  );
}

export default function OfferedVolumeEditor({ me, onRefresh }) {
  const { t } = useI18n();
  const [coeffCfg, setCoeffCfg] = useState(null);
  useEffect(() => {
    api.get("/config/wager-coefficients").then((r) => setCoeffCfg(r.data)).catch(() => {});
  }, []);
  const weightedCasino = weightedFor("casino", me, coeffCfg);
  const weightedCase = weightedFor("case_opening", me, coeffCfg);
  const caseWager = Number(me?.caseWagerSelfReported || 0);
  const offered = me?.offeredVolumeByVertical || {};
  const caseLaunched = Number(caseWager) > 0 || offered.case_opening;   // conservative — only show case row if the player has case data
  return (
    <section className="lg:col-span-12 border border-[#F5B841]/25 bg-[#F5B841]/[0.04] p-6 lg:p-8" data-testid="dash-offered-tile">
      <div className="flex items-center gap-2 mb-1"><Coins className="w-4 h-4 text-[#F5B841]" /><h2 className="font-display font-bold text-lg">{t("dash.offered.title")}</h2></div>
      <p className="text-xs text-[#8B97AE] mb-4 max-w-3xl">{t("dash.offered.body")}</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <VerticalEditor vertical="casino" weighted={weightedCasino} current={Number(offered.casino || 0)} onSaved={onRefresh} t={t} />
        {caseLaunched && (
          <VerticalEditor vertical="case_opening" weighted={weightedCase} current={Number(offered.case_opening || 0)} onSaved={onRefresh} t={t} />
        )}
      </div>
      <div className="mt-4 flex items-start gap-2 text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
        <Lock className="w-3 h-3 mt-0.5 shrink-0" />
        <span>{t("dash.offered.note")}</span>
      </div>
    </section>
  );
}
